import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Grid,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from "@mui/material";
import {
  Receipt,
  CalendarToday,
  Payment,
  LocalShipping,
  Visibility,
  Refresh,
  Close,
  Store,
  CheckCircle,
  Pending,
  Error,
  FilterList,
} from "@mui/icons-material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { id } from "date-fns/locale";
import Nav from "components/Nav";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import axios from "axios";
import BASE_URL from "config/Base_URL";

const API_URL = `${BASE_URL}/purchase/user`;

export default function OrderHistoryPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  // filter tanggal
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const fetchOrderHistory = async () => {
    try {
      setLoading(true);
      setError("");
      const token = sessionStorage.getItem("token");

      const response = await axios.get(API_URL, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (response.data && response.data.data) {
        setOrders(response.data.data);
      }
    } catch (err) {
      console.error("Gagal mengambil riwayat pesanan:", err);
      setError("Gagal memuat riwayat pesanan. Silakan coba lagi.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderHistory();
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const options = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return new Date(dateString).toLocaleDateString("id-ID", options);
  };

  const getPaymentStatusIcon = (status) => {
    switch (status) {
      case "LUNAS":
        return <CheckCircle sx={{ fontSize: 18, color: "success.main" }} />;
      case "UTANG":
        return <Pending sx={{ fontSize: 18, color: "warning.main" }} />;
      case "GAGAL":
        return <Error sx={{ fontSize: 18, color: "error.main" }} />;
      default:
        return <Payment sx={{ fontSize: 18 }} />;
    }
  };

  const getDeliveryStatusIcon = (status) => {
    switch (status) {
      case "SELESAI":
        return <CheckCircle sx={{ fontSize: 18, color: "success.main" }} />;
      case "PENGIRIMAN":
        return <LocalShipping sx={{ fontSize: 18, color: "info.main" }} />;
      case "GAGAL":
        return <Error sx={{ fontSize: 18, color: "error.main" }} />;
      default:
        return <LocalShipping sx={{ fontSize: 18 }} />;
    }
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setDetailDialogOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailDialogOpen(false);
    setSelectedOrder(null);
  };

  // filter data orders berdasarkan tanggal
  const filteredOrders = orders.filter((order) => {
    if (!startDate && !endDate) return true;
    const orderDate = new Date(order.date);
    if (startDate && orderDate < startDate) return false;
    if (endDate && orderDate > endDate) return false;
    return true;
  });

  if (loading) {
    return (
      <>
        <Nav />
        <Container
          maxWidth="lg"
          sx={{
            py: 4,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "50vh",
          }}
        >
          <MDBox textAlign="center">
            <CircularProgress size={60} thickness={4} sx={{ mb: 2, color: "primary.main" }} />
            <MDTypography variant="h6" color="text.secondary">
              Memuat riwayat pesanan...
            </MDTypography>
          </MDBox>
        </Container>
      </>
    );
  }

  return (
    <>
      <Nav />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <MDTypography variant="h4" component="h1" fontWeight="bold" color="primary">
            Riwayat Pesanan
          </MDTypography>
          <MDButton
            variant="gradient"
            color="info"
            startIcon={<Refresh />}
            onClick={fetchOrderHistory}
            disabled={loading}
          >
            Refresh
          </MDButton>
        </MDBox>

        {/* Filter Tanggal */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <MDBox display="flex" alignItems="center" gap={2}>
            <FilterList color="action" />
            <Typography variant="subtitle1" fontWeight="bold">
              Filter Tanggal:
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={id}>
              <DatePicker
                label="Dari Tanggal"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                slotProps={{ textField: { size: "small" } }}
              />
              <DatePicker
                label="Sampai Tanggal"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                slotProps={{ textField: { size: "small" } }}
              />
            </LocalizationProvider>
            {startDate || endDate ? (
              <Button
                onClick={() => {
                  setStartDate(null);
                  setEndDate(null);
                }}
              >
                Reset
              </Button>
            ) : null}
          </MDBox>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {filteredOrders.length === 0 && !error ? (
          <Paper
            sx={{
              p: 4,
              textAlign: "center",
              borderRadius: 2,
              boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
            }}
          >
            <Receipt sx={{ fontSize: 60, color: "text.secondary", mb: 2 }} />
            <MDTypography variant="h6" color="text.secondary" gutterBottom>
              Tidak ada pesanan untuk rentang tanggal ini
            </MDTypography>
            <MDTypography variant="body2" color="text.secondary">
              Silakan ubah filter tanggal untuk melihat pesanan lain.
            </MDTypography>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {filteredOrders.map((order) => (
              <Grid item xs={12} key={order.invoiceNumber}>
                <Card
                  elevation={2}
                  sx={{
                    borderRadius: 2,
                    "&:hover": {
                      boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                    },
                    transition: "box-shadow 0.2s",
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      mb={2}
                    >
                      <MDTypography variant="h6" fontWeight="bold" color="primary">
                        {order.invoiceNumber}
                      </MDTypography>
                      <MDBox display="flex" alignItems="center">
                        <CalendarToday sx={{ fontSize: 14, mr: 0.5, color: "text.secondary" }} />
                        <MDTypography variant="caption" color="text.secondary">
                          {formatDate(order.date)}
                        </MDTypography>
                      </MDBox>
                    </MDBox>

                    <MDBox display="flex" alignItems="center" mb={1.5}>
                      <Store sx={{ fontSize: 16, mr: 1, color: "text.secondary" }} />
                      <MDTypography variant="body2" fontWeight="medium">
                        {order.name}
                      </MDTypography>
                    </MDBox>

                    <Divider sx={{ my: 1.5 }} />

                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1.5}
                    >
                      <MDTypography variant="body2">Total:</MDTypography>
                      <MDTypography variant="body1" fontWeight="bold" color="primary">
                        {formatCurrency(order.totalPrice)}
                      </MDTypography>
                    </MDBox>

                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1.5}
                    >
                      <MDBox display="flex" alignItems="center">
                        <Payment sx={{ fontSize: 16, mr: 1, color: "text.secondary" }} />
                        <MDTypography variant="body2">Pembayaran:</MDTypography>
                      </MDBox>
                      <MDBox display="flex" alignItems="center">
                        {getPaymentStatusIcon(order.paymentStatus)}
                        <MDTypography
                          variant="body2"
                          sx={{
                            ml: 0.5,
                            color:
                              order.paymentStatus === "LUNAS"
                                ? "success.main"
                                : order.paymentStatus === "UTANG"
                                ? "warning.main"
                                : order.paymentStatus === "GAGAL"
                                ? "error.main"
                                : "text.primary",
                          }}
                        >
                          {order.paymentStatus}
                        </MDTypography>
                      </MDBox>
                    </MDBox>

                    <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <MDBox display="flex" alignItems="center">
                        <LocalShipping sx={{ fontSize: 16, mr: 1, color: "text.secondary" }} />
                        <MDTypography variant="body2">Pengiriman:</MDTypography>
                      </MDBox>
                      <MDBox display="flex" alignItems="center">
                        {getDeliveryStatusIcon(order.deliveryStatus)}
                        <MDTypography
                          variant="body2"
                          sx={{
                            ml: 0.5,
                            color:
                              order.deliveryStatus === "SELESAI"
                                ? "success.main"
                                : order.deliveryStatus === "PENGIRIMAN"
                                ? "info.main"
                                : order.deliveryStatus === "GAGAL"
                                ? "error.main"
                                : "text.primary",
                          }}
                        >
                          {order.deliveryStatus}
                        </MDTypography>
                      </MDBox>
                    </MDBox>

                    <MDBox display="flex" justifyContent="flex-end">
                      <MDButton
                        variant="outlined"
                        color="primary"
                        size="small"
                        startIcon={<Visibility />}
                        onClick={() => handleViewDetails(order)}
                        sx={{ borderRadius: 2 }}
                      >
                        Detail
                      </MDButton>
                    </MDBox>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Dialog Detail Pesanan */}
        <Dialog open={detailDialogOpen} onClose={handleCloseDetails} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" fontWeight="bold">
                Detail Pesanan: {selectedOrder?.invoiceNumber}
              </Typography>
              <IconButton onClick={handleCloseDetails} size="small">
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {selectedOrder && (
              <Box>
                <Grid container spacing={2} mb={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Tanggal Pesanan
                    </Typography>
                    <Typography variant="body2">{formatDate(selectedOrder.date)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Nama Toko
                    </Typography>
                    <Typography variant="body2">{selectedOrder.name}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Total Pembayaran
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" color="primary">
                      {formatCurrency(selectedOrder.totalPrice)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center">
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ mr: 1 }}>
                        Status Pembayaran:
                      </Typography>
                      {getPaymentStatusIcon(selectedOrder.paymentStatus)}
                      <Typography
                        variant="body2"
                        sx={{
                          ml: 0.5,
                          color:
                            selectedOrder.paymentStatus === "LUNAS"
                              ? "success.main"
                              : selectedOrder.paymentStatus === "UTANG"
                              ? "warning.main"
                              : selectedOrder.paymentStatus === "GAGAL"
                              ? "error.main"
                              : "text.primary",
                        }}
                      >
                        {selectedOrder.paymentStatus}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center">
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ mr: 1 }}>
                        Status Pengiriman:
                      </Typography>
                      {getDeliveryStatusIcon(selectedOrder.deliveryStatus)}
                      <Typography
                        variant="body2"
                        sx={{
                          ml: 0.5,
                          color:
                            selectedOrder.deliveryStatus === "SELESAI"
                              ? "success.main"
                              : selectedOrder.deliveryStatus === "PENGIRIMAN"
                              ? "info.main"
                              : selectedOrder.deliveryStatus === "GAGAL"
                              ? "error.main"
                              : "text.primary",
                        }}
                      >
                        {selectedOrder.deliveryStatus}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Informasi Tambahan
                </Typography>

                <Box mt={1}>
                  <Typography variant="body2" color="text.secondary">
                    Untuk informasi detail item pesanan, silakan hubungi penjual langsung.
                  </Typography>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDetails} color="primary">
              Tutup
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
}
